// src/components/IntensivePrayerPresentation.js
// Prayer slideshow modal for Pre-Marriage Intensive modules
// Visual Theme: Deep purple/indigo for prayer atmosphere
// Supports two prayer modes: Long (5 prayers) and Short (7 prayer points)

import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Heart, BookOpen, Zap } from 'lucide-react';
import { INTENSIVE_PRAYER } from '../data/intensivePrayer';
import { INTENSIVE_PRAYER_2 } from '../data/intensivePrayer2';

const IntensivePrayerPresentation = ({ moduleNumber, onClose, initialMode = 'long' }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prayerMode, setPrayerMode] = useState(initialMode); // 'long' or 'short'
  
  // Get prayer data based on mode
  const prayerData = prayerMode === 'long' 
    ? INTENSIVE_PRAYER[moduleNumber] 
    : INTENSIVE_PRAYER_2[moduleNumber];

  // Reset slide when switching modes
  useEffect(() => {
    setCurrentSlide(0);
  }, [prayerMode]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      e.preventDefault();
      nextSlide();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prevSlide();
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [currentSlide, prayerMode]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!prayerData || !prayerData.prayers) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 text-center max-w-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Prayers Not Available</h2>
          <p className="text-gray-600 mb-6">Prayer content for this module is not available yet.</p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Create slides array based on mode
  const slides = prayerMode === 'long'
    ? [
        { type: 'intro' },
        ...prayerData.prayers.map(prayer => ({ type: 'prayer', ...prayer })),
        { type: 'closing' }
      ]
    : [
        { type: 'intro' },
        ...prayerData.prayers.map(prayer => ({ type: 'short-prayer', ...prayer })),
        { type: 'closing' }
      ];

  const slide = slides[currentSlide];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Toggle between prayer modes
  const togglePrayerMode = () => {
    setPrayerMode(prev => prev === 'long' ? 'short' : 'long');
  };

  const renderSlide = () => {
    switch (slide.type) {
      case 'intro':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 sm:px-8">
            <div className="text-5xl sm:text-6xl md:text-7xl mb-6">🙏</div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {prayerData.factorName}
            </h1>
            <h2 className="text-lg sm:text-xl md:text-2xl text-purple-200 mb-6">
              Module {moduleNumber}: {prayerData.moduleName}
            </h2>
            
            {/* Prayer Mode Badge */}
            <div className={`mb-4 px-4 py-2 rounded-full text-sm font-medium ${
              prayerMode === 'long' 
                ? 'bg-purple-500/30 text-purple-200 border border-purple-400/30'
                : 'bg-amber-500/30 text-amber-200 border border-amber-400/30'
            }`}>
              {prayerMode === 'long' ? (
                <span className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Deep Prayers • {prayerData.prayers.length} Prayers
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Quick Prayer Points • {prayerData.prayers.length} Points
                </span>
              )}
            </div>
            
            {prayerMode === 'long' && prayerData.foundationScripture && (
              <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20 mb-6">
                <p className="text-sm sm:text-base md:text-lg text-amber-200 italic leading-relaxed">
                  {prayerData.foundationScripture}
                </p>
              </div>
            )}

            {prayerMode === 'long' && prayerData.introduction && (
              <div className="max-w-xl mx-auto">
                <p className="text-sm sm:text-base text-purple-100 leading-relaxed">
                  {prayerData.introduction}
                </p>
              </div>
            )}

            {prayerMode === 'short' && (
              <div className="max-w-xl mx-auto p-4 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <p className="text-sm sm:text-base text-purple-100 leading-relaxed">
                  {prayerData.prayers.length} powerful prayer points with Scripture for Module {moduleNumber}. 
                  Each prayer is concise and focused, perfect for daily devotion or quick intercession.
                </p>
              </div>
            )}

            <div className="mt-8 text-purple-300 text-sm animate-pulse">
              Press → or Space to begin prayers
            </div>
          </div>
        );

      case 'prayer':
        // Long prayer format
        return (
          <div className="flex flex-col h-full px-4 sm:px-6 md:px-8 py-4 overflow-y-auto">
            {/* Prayer Header */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-purple-500/30 rounded-full mb-3">
                <span className="text-xl sm:text-2xl font-bold text-white">{slide.number}</span>
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                {slide.title}
              </h2>
            </div>

            {/* Prayer Content */}
            <div className="flex-1 max-w-3xl mx-auto w-full">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 md:p-8 border border-white/20">
                <p className="text-sm sm:text-base md:text-lg text-white leading-relaxed whitespace-pre-line">
                  {slide.prayer}
                </p>
              </div>
            </div>

            {/* Scripture Reference */}
            {slide.scripture && (
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-amber-900/30 rounded-lg border border-amber-500/30 max-w-3xl mx-auto w-full">
                <p className="text-sm sm:text-base text-amber-200 italic text-center">
                  📖 {slide.scripture}
                </p>
              </div>
            )}
          </div>
        );

      case 'short-prayer':
        // Short prayer point format
        return (
          <div className="flex flex-col items-center justify-center h-full px-4 sm:px-6 md:px-8 py-4">
            {/* Prayer Number */}
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-amber-500/30 rounded-full mb-6 border-2 border-amber-400/50">
              <span className="text-3xl sm:text-4xl font-bold text-amber-300">{slide.number}</span>
            </div>

            {/* Prayer Point */}
            <div className="max-w-3xl mx-auto w-full mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-10 border border-white/20 text-center">
                <p className="text-xl sm:text-2xl md:text-3xl text-white leading-relaxed font-medium">
                  {slide.prayer}
                </p>
              </div>
            </div>

            {/* Scripture Reference */}
            <div className="max-w-2xl mx-auto w-full p-4 sm:p-6 bg-amber-900/40 rounded-xl border border-amber-500/30">
              <p className="text-amber-300 font-semibold text-center mb-2 text-sm sm:text-base">
                📖 {slide.scripture}
              </p>
              <p className="text-sm sm:text-base md:text-lg text-amber-100 italic text-center leading-relaxed">
                "{slide.scriptureText}"
              </p>
            </div>
          </div>
        );

      case 'closing':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 sm:px-8">
            <div className="text-5xl sm:text-6xl md:text-7xl mb-6">✨</div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-6">
              Prayer Session Complete
            </h2>
            
            <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20 mb-8">
              <p className="text-sm sm:text-base md:text-lg text-purple-100 leading-relaxed">
                {prayerMode === 'long' && prayerData.closing 
                  ? prayerData.closing 
                  : `You have completed all ${prayerData.prayers.length} prayer points for Module ${moduleNumber}. Let these prayers guide your journey toward a Kingdom marriage.`
                }
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setCurrentSlide(1)}
                className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition"
              >
                🔄 Pray Again
              </button>
              <button
                onClick={togglePrayerMode}
                className={`px-6 py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                  prayerMode === 'long'
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'bg-purple-500 hover:bg-purple-600 text-white'
                }`}
              >
                {prayerMode === 'long' ? (
                  <>
                    <Zap className="w-4 h-4" />
                    Try Short Prayers
                  </>
                ) : (
                  <>
                    <BookOpen className="w-4 h-4" />
                    Try Deep Prayers
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition"
              >
                ✓ Finish
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-800">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-black/20">
        <div className="flex items-center gap-2 sm:gap-3">
          <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-pink-400" />
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-white">
              Module {moduleNumber} Prayers
            </h3>
            <p className="text-xs text-purple-300 hidden sm:block">
              {prayerData.moduleName}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Prayer Mode Toggle */}
          <button
            onClick={togglePrayerMode}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
              prayerMode === 'long'
                ? 'bg-purple-500/40 text-purple-200 hover:bg-purple-500/60'
                : 'bg-amber-500/40 text-amber-200 hover:bg-amber-500/60'
            }`}
          >
            {prayerMode === 'long' ? (
              <>
                <BookOpen className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Deep</span>
              </>
            ) : (
              <>
                <Zap className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Quick</span>
              </>
            )}
          </button>

          <span className="text-xs sm:text-sm text-purple-200">
            {slide.type === 'intro' ? 'Introduction' : 
             slide.type === 'closing' ? 'Closing' :
             `Prayer ${slide.number} of ${prayerData.prayers.length}`}
          </span>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 bg-white/20">
        <div
          className={`h-full transition-all duration-300 ${
            prayerMode === 'long' ? 'bg-pink-400' : 'bg-amber-400'
          }`}
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Slide Content */}
      <div className="flex-1 overflow-hidden">
        {renderSlide()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-black/20">
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition ${
            currentSlide === 0
              ? 'bg-white/10 text-white/50 cursor-not-allowed'
              : 'bg-white/20 text-white hover:bg-white/30'
          }`}
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Slide Dots */}
        <div className="flex gap-1 sm:gap-1.5 overflow-x-auto max-w-[50%] justify-center">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition flex-shrink-0 ${
                index === currentSlide
                  ? prayerMode === 'long' ? 'bg-pink-400' : 'bg-amber-400'
                  : index < currentSlide
                    ? 'bg-purple-500'
                    : 'bg-white/30'
              }`}
            />
          ))}
        </div>

        <button
          onClick={currentSlide === slides.length - 1 ? onClose : nextSlide}
          className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-white rounded-lg font-medium transition ${
            prayerMode === 'long' 
              ? 'bg-purple-500 hover:bg-purple-600' 
              : 'bg-amber-500 hover:bg-amber-600'
          }`}
        >
          <span className="hidden sm:inline">
            {currentSlide === slides.length - 1 ? 'Finish' : 'Next'}
          </span>
          <span className="sm:hidden">
            {currentSlide === slides.length - 1 ? '✓' : '→'}
          </span>
          {currentSlide < slides.length - 1 && (
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          )}
        </button>
      </div>

      {/* Keyboard hints */}
      <div className="hidden md:flex justify-center py-2 bg-black/10 text-xs text-purple-300/60">
        <span>Use ← → arrow keys or spacebar to navigate • Press ESC to close</span>
      </div>
    </div>
  );
};

export default IntensivePrayerPresentation;
